import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, Compass, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { LazyImage } from '@/components/ui/lazy-image';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { RichTextViewer } from '@/components/ui/rich-text-viewer/RichTextViewer';

interface Tag {
  id: string;
  name: string;
}

interface Itinerary {
  id: string;
  title: string;
  destination: string | null;
  duration: number | null;
  best_season: string | null;
  description: string | null;
  featured_image: string | null;
  tags: Tag[];
}

export default function PublicItineraries() {
  const [itineraries, setItineraries] = useState<Itinerary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const renderCount = React.useRef(0);
  
  useEffect(() => {
    renderCount.current += 1;
    console.log(`[DEBUG] PublicItineraries RENDER #${renderCount.current}`);
  });

  useEffect(() => {
    async function fetchItineraries() {
      try {
        setLoading(true);
        console.log('[DEBUG] PublicItineraries - Starting to fetch itineraries');
        
        const { data: itineraryData, error: itineraryError } = await supabase
          .from('itineraries')
          .select('id, title, destination, duration, best_season, description, featured_image')
          .eq('status', 'published')
          .order('title');

        if (itineraryError) {
          console.error('[DEBUG] PublicItineraries - Error fetching itineraries:', itineraryError);
          throw itineraryError;
        }
        
        console.log('[DEBUG] PublicItineraries - Fetched itineraries data:', itineraryData?.length || 0, 'items');
        
        if (itineraryData) {
          const itinerariesWithTags = await Promise.all(
            itineraryData.map(async (itinerary) => {
              console.log(`[DEBUG] PublicItineraries - Fetching tags for itinerary: ${itinerary.id}`);
              const { data: tagData, error: tagError } = await supabase
                .from('itinerary_tags')
                .select('tags:tag_id(id, name)')
                .eq('itinerary_id', itinerary.id);
                
              if (tagError) {
                console.error(`[DEBUG] PublicItineraries - Error fetching tags for itinerary ${itinerary.id}:`, tagError);
              }
                
              const tags = tagData 
                ? tagData.map(item => (item.tags as unknown as Tag)) 
                : [];
                
              console.log(`[DEBUG] PublicItineraries - Fetched ${tags.length} tags for itinerary: ${itinerary.id}`);
              
              return {
                ...itinerary,
                tags
              };
            })
          );
          
          console.log('[DEBUG] PublicItineraries - Completed processing all itineraries with tags');
          setItineraries(itinerariesWithTags);
        }
      } catch (err: any) {
        console.error('[DEBUG] PublicItineraries - Critical error fetching itineraries:', err.message, err);
        setError('Failed to load itineraries. Please try again later.');
      } finally {
        console.log('[DEBUG] PublicItineraries - Finished fetch operation, setting loading to false');
        setLoading(false);
      }
    }

    fetchItineraries();
  }, []);

  const fallbackItineraries = [
    {
      id: '1',
      title: 'Ultimate Bali Adventure',
      destination: 'Bali, Indonesia',
      duration: 7,
      best_season: 'April - October',
      description: 'Explore the best of Bali from pristine beaches to lush rice terraces and cultural temples.',
      featured_image: 'https://images.unsplash.com/photo-1472396961693-142e6e269027',
      tags: [{ id: '1', name: 'Adventure' }, { id: '2', name: 'Beach' }, { id: '3', name: 'Culture' }]
    },
    {
      id: '2',
      title: 'Kyoto Cultural Experience',
      destination: 'Kyoto, Japan',
      duration: 5,
      best_season: 'March - May (Cherry Blossoms)',
      description: 'Experience traditional Japan with temples, tea ceremonies, and beautiful gardens.',
      featured_image: 'https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07',
      tags: [{ id: '4', name: 'Culture' }, { id: '5', name: 'History' }, { id: '6', name: 'Food' }]
    },
    {
      id: '3',
      title: 'Swiss Alps Hiking Tour',
      destination: 'Interlaken, Switzerland',
      duration: 6,
      best_season: 'June - September',
      description: 'Trek through stunning alpine scenery with breathtaking mountain views.',
      featured_image: 'https://images.unsplash.com/photo-1482938289607-e9573fc25ebb',
      tags: [{ id: '7', name: 'Hiking' }, { id: '8', name: 'Mountains' }, { id: '9', name: 'Nature' }]
    },
    {
      id: '4',
      title: 'Greek Island Hopping',
      destination: 'Santorini & Mykonos, Greece',
      duration: 8,
      best_season: 'May - September',
      description: 'Experience the best of Greek island life with beautiful beaches and charming villages.',
      featured_image: 'https://images.unsplash.com/photo-1433086966358-54859d0ed716',
      tags: [{ id: '10', name: 'Beach' }, { id: '11', name: 'Island' }, { id: '12', name: 'Culture' }]
    }
  ];

  const displayItineraries = itineraries.length > 0 ? itineraries : fallbackItineraries;
  
  // Check if any itinerary has China tag
  const hasChinaItineraries = itineraries.some(i => 
    i.tags?.some(t => t.name.toLowerCase().includes('china'))
  );

  return (
    <div className="container py-8 md:py-12">
      <div className="text-center mb-10">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">Explore Travel Itineraries</h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Browse our collection of carefully crafted travel itineraries to help you plan your next adventure.
          Each itinerary includes day-by-day plans, recommended activities, and insider tips.
        </p>
      </div>

      {/* Quick Filters */}
      <div className="flex flex-wrap gap-2 mb-8 justify-center">
        <Link 
          to="/china/itineraries"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 hover:bg-primary/20 text-primary font-medium transition-colors"
        >
          🇨🇳 China Itineraries
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-10 w-10 animate-spin text-primary" />
        </div>
      ) : error ? (
        <div className="text-center text-red-500 py-10">
          <p>{error}</p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={() => window.location.reload()}
          >
            Try Again
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {displayItineraries.map((itinerary) => (
            <Card key={itinerary.id} className="overflow-hidden group h-full hover:shadow-md transition-shadow">
              <div className="aspect-video relative overflow-hidden">
                <LazyImage
                  src={itinerary.featured_image || 'https://images.unsplash.com/photo-1482938289607-e9573fc25ebb'}
                  alt={itinerary.title}
                  className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <CardContent className="p-5">
                <h2 className="text-xl font-semibold mb-2">{itinerary.title}</h2>
                
                <div className="flex flex-wrap gap-y-2 mb-3">
                  {itinerary.destination && (
                    <div className="flex items-center text-sm text-muted-foreground mr-4">
                      <MapPin className="h-4 w-4 mr-1" />
                      <span>{itinerary.destination}</span>
                    </div>
                  )}
                  {itinerary.duration && (
                    <div className="flex items-center text-sm text-muted-foreground mr-4">
                      <Clock className="h-4 w-4 mr-1" />
                      <span>{itinerary.duration} days</span>
                    </div>
                  )}
                  {itinerary.best_season && (
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>{itinerary.best_season}</span>
                    </div>
                  )}
                </div>
                
                {itinerary.description && (
                  <div className="text-muted-foreground">
                    <RichTextViewer content={itinerary.description} />
                  </div>
                )}
                
                <div className="flex flex-wrap gap-2 mt-4">
                  {itinerary.tags && itinerary.tags.map(tag => (
                    <Badge key={tag.id} variant="secondary">{tag.name}</Badge>
                  ))}
                </div>
              </CardContent>
              <CardFooter className="pt-0 px-5 pb-5">
                <Link to={`/itineraries/${itinerary.id}`} className="flex items-center text-primary hover:underline">
                  <span>View itinerary</span>
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      <div className="text-center mt-10">
        <Button variant="outline" disabled={loading}>
          {itineraries.length > 0 ? "More itineraries coming soon" : "Loading itineraries..."}
        </Button>
      </div>
    </div>
  );
}
